import { StateCreator } from 'zustand';
import { persist, PersistOptions, StorageValue } from 'zustand/middleware';
import { createWithEqualityFn } from 'zustand/traditional';

import { getPost as restGetPost } from '../../api/Rest/post';
import { Post } from '../../models/PostStore/Post';
import {
  PostListType,
  PostRepository,
  PostStoreAction,
  PostStoreState,
  PostStoreStateJson,
} from '../../models/PostStore/PostStore';
import { SpaceMembership } from '../../models/SpaceStore';
import {
  AlbumListType,
  SpaceListType,
  SpaceMembershipMapType,
} from '../../models/SpaceStore/SpaceStore';
import {
  createStoreListFieldHook,
  createStoreMapFieldHook,
  StoreLifecycle,
  StoreLifecycleAction,
} from '../../models/Store';
import { Profile, UserListType } from '../../models/UserStore';
import { dateNumberReviver } from '../../utils/json';
import { useSpaceStore } from '../SpaceStore';
import { useUserStore } from '../UserStore';

import {
  GameListType,
  MultiplayerGameListType,
  MultiplayerGameSessionListType,
} from './../../models/SpaceStore/SpaceStore';
import * as PostStore from './state';

type Store = StoreLifecycleAction & PostStoreState & PostStoreAction;

const lifecycle = new StoreLifecycle('PostStore');

const initializer: StateCreator<Store> = (set, get) => {
  const initialize = lifecycle.createInitialize(
    () => {
      // do nothing
    },
    () => set((state) => ({ ...state })),
  );

  const shutdown = lifecycle.createShutdown(
    () => {
      // clear data
      const state = {
        posts: new PostRepository(),
      };

      set(state);
    },
    () => set((state) => ({ ...state })),
  );

  const getPost: PostStoreAction['getPost'] = (postId) => {
    return get().posts.get(postId);
  };

  const fetchPost: PostStoreAction['fetchPost'] = async (postId) => {
    const response = await restGetPost({ postId }, { withProfiles: true });
    const { ok, data } = response;

    if (ok) {
      const space = data.channel.classroom;
      const profiles = data.profiles;
      const membership = data.channel.membership;
      const album = data.data.gameSet;
      const game = data.data.game;
      const sharingSpace = data.data.classroom;

      const multiplayerGame = data.data.multiplayerGame;
      const multiplayerGameSession = data.data.multiplayerGameSession;

      set((state) => {
        const newPost = new Post(data.post);
        const updatePosts = [newPost];

        if (data.data.post) {
          const subPost = new Post(data.data.post);
          updatePosts.push(subPost);
        }

        PostStore.setPosts(state, PostListType.Recent, ...updatePosts);

        return { ...state };
      });
      if (multiplayerGame) {
        useSpaceStore
          .getState()
          .addMultiplayerGameToField(
            MultiplayerGameListType.Recent,
            multiplayerGame,
          );
      }

      if (multiplayerGameSession) {
        useSpaceStore
          .getState()
          .addMultiplayerGameSessionToField(
            MultiplayerGameSessionListType.Recent,
            multiplayerGameSession,
          );
      }

      if (sharingSpace) {
        useSpaceStore
          .getState()
          .addSpaceToField(SpaceListType.Recent, sharingSpace);
      }

      if (space) {
        useSpaceStore.getState().addSpaceToField(SpaceListType.Recent, space);
      }

      if (album) {
        useSpaceStore.getState().addAlbumToField(AlbumListType.Recent, album);
      }

      if (profiles) {
        useUserStore.getState().addProfiles(
          UserListType.Recent,
          ...profiles.map((p) => {
            return new Profile(p.profile);
          }),
        );
      }

      if (game) {
        useSpaceStore.getState().addGameToField(GameListType.Recent, game);
      }

      if (membership) {
        useSpaceStore
          .getState()
          .spaceMemberships.map(SpaceMembershipMapType.Recent)
          .set(
            `${membership.classroomId}_${membership.userId}`,
            new SpaceMembership(membership),
          );
      }

      const nestChannelClassroom = data.data.channel.classroom;
      if (nestChannelClassroom) {
        useSpaceStore
          .getState()
          .addSpaceToField(SpaceListType.Recent, nestChannelClassroom);
      }
    } else {
      console.error('Failed to fetch post');
    }
  };

  return {
    initialize,
    shutdown,
    posts: new PostRepository(),
    getPost,
    fetchPost,
  };
};

const persistOptions: PersistOptions<Store, Partial<PostStoreState>> = {
  name: 'post-storage',
  // getStorage: () => AsyncStorage,
  deserialize: (str: string): StorageValue<Partial<PostStoreState>> => {
    try {
      const { state, version } = JSON.parse(
        str,
        dateNumberReviver,
      ) as StorageValue<PostStoreStateJson>;
      return {
        state: {
          posts: new PostRepository(state.posts),
        },
        version,
      };
    } catch (err) {
      console.error('Fail to deserialize post-storage', str);
      console.error(err);
      return { state: {} };
    }
  },
  onRehydrateStorage: () => {
    return (state) => {
      if (state) {
        // state.posts.onElementPrune = state.onPostPrune;
      }
      lifecycle.finishHydration();
      // TODO: handle rehydrate error case by shutdown?
    };
  },
};

export const usePostStore = createWithEqualityFn<Store>()(
  persist(initializer, persistOptions),
  Object.is,
);
export const usePostStoreListField = createStoreListFieldHook(usePostStore);
export const usePostStoreMapField = createStoreMapFieldHook(usePostStore);
